// schema
import * as ISchema from "@schema";

import React from "react";
import { connect } from "react-redux";
import { createRefetchContainer, RelayRefetchProp } from "react-relay";

import { IOrg } from "@schema";

import { snackBarOpen } from "@actions/snack-bar.actions";
import { IReduxState } from "@reducers/root.reducer";

import {
  ssoAdminPortalButtonFragments,
  ssoAdminPortalButtonQuery,
} from "./SsoAdminPortalButton.gql";

import { getValue } from "@util/errorable.util";
import { track, TrackingEvents } from "@src/util/analytics.util";

import { Button, Tooltip, makeStyles } from "@material-ui/core";

import { greys } from "@clockwise/web-commons/src/styles/color.styles";
import { logger } from "@src/util/logger.util";

export interface IAdminPanelBillingGroupsPlansAndBillingCmpt {
  billingGroup: ISchema.BillingGroupErrorable;
  org: IOrg;
  disabled?: boolean;
  relay: RelayRefetchProp;
}

interface IAdminPanelBillingGroupsPlansAndBillingProps
  extends IAdminPanelBillingGroupsPlansAndBillingCmpt {
  snackBarOpen: (message: string) => void;
}

const useStyles = makeStyles({
  primaryBtn: {
    color: greys.white,
  },
});

const AdminPanelBillingGroupsPlansAndBillingCmpt = (
  props: IAdminPanelBillingGroupsPlansAndBillingProps,
) => {
  const classes = useStyles();

  // ~-~-~-~-~-~-~-
  // State
  // ~-~-~-~-~-~-~-
  const [generatingLink, setGeneratingLink] = React.useState(false);

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  React.useEffect(() => {
    const billingGroup = getValue(props.billingGroup);
    const ssoAdminPortalLink = billingGroup ? getValue(billingGroup.ssoAdminPortalLink) : null;

    if (!generatingLink) {
      return;
    } else if (!ssoAdminPortalLink) {
      return logger.error("failed to get ssoAdminPortalLink from billing group");
    }

    track(TrackingEvents.ADMIN_PANEL.PLANS_AND_BILLING_CLICK_CONFIGURE_SCIM);
    window.open(ssoAdminPortalLink.link, "_blank");

    setGeneratingLink(false);
  }, [props.billingGroup]);

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  const onClickConfigureSso = () => {
    setGeneratingLink(true);

    const billingGroup = getValue(props.billingGroup);

    if (!billingGroup) {
      return logger.error("failed to call sso admin portal button refetch");
    }

    props.relay.refetch(
      {
        billingGroupId: billingGroup.id,
        orgRelayId: props.org.id,
      },
      undefined,
      (error?: Error) => {
        if (error) {
          logger.error("failed to generate sso admin link");
        }
      },
      { force: true },
    );
  };

  return (
    <>
      <Tooltip
        title={
          props.disabled
            ? `You don't have permission. To configure SCIM, please contact a plan admin.`
            : ""
        }
      >
        <Button
          color="primary"
          variant="contained"
          classes={{ root: classes.primaryBtn }}
          onClick={() => onClickConfigureSso()}
          disabled={generatingLink || props.disabled}
        >
          {generatingLink ? "Opening portal" : "Configure SCIM"}
        </Button>
      </Tooltip>
    </>
  );
};

function mapStateToProps(
  _state: IReduxState,
  _ownProps: IAdminPanelBillingGroupsPlansAndBillingCmpt,
) {
  return {};
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(AdminPanelBillingGroupsPlansAndBillingCmpt);

export const SsoAdminPortalButton = createRefetchContainer(
  reduxContainer,
  ssoAdminPortalButtonFragments,
  ssoAdminPortalButtonQuery,
);
